import { useReadOnly } from '../../Hooks/useReadOnly';
import { Permissions } from '../../Security/permissions';
import React, { useState, useEffect } from 'react';
import { Button, Container, Row, Col, Modal, Form } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { LicenseDataService } from '../../Services/LicenseDataService';
import Table from '../Table/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { formatDate } from '../../Utils/formaters';
import { UsersDataService } from '../../Services/UsersDataService';
import { Link } from 'react-router-dom';
import { useSecurity } from '../../Contexts/SecurityContext';

const LicenseManagement = () => {
    const readOnly = useReadOnly(Permissions.Security.Licenses);
    const { instance, accounts, inProgress } = useMsal();
    const { hasPermission } = useSecurity();
    const canListUsers = hasPermission(Permissions.Security.Users);
    const [licenses, setLicenses] = useState([]); // Changed to array for multiple licenses
    const [licenseAssignations, setLicenseAssignations] = useState([]);
    const [users, setUsers] = useState([]); // New state for users
    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [showAddLicenseModal, setShowAddLicenseModal] = useState(false); // State for Add License Modal
    const [newLicenseKey, setNewLicenseKey] = useState(''); // Track new license key
    const [selectedUserId, setSelectedUserId] = useState(''); // Track selected user
    const [selectedRole, setSelectedRole] = useState(''); // Track selected role
    const [selectedLicense, setSelectedLicense] = useState('');

    useEffect(() => {
        if (inProgress === 'none' && accounts.length > 0) {
            fetchLicenses();
            if (canListUsers) fetchUsers();
        }
    }, [inProgress, accounts, canListUsers]);

    const fetchLicenses = async () => {
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const response = await instance.acquireTokenSilent(request);
            const data = await LicenseDataService.getLicenses(response.accessToken);
            setLicenses(data);
            setLicenseAssignations(data.flatMap(license => license.licenses)); // Combine license assignments
        } catch (error) {
            console.error('Failed to fetch licenses:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const response = await instance.acquireTokenSilent(request);
            const userData = await UsersDataService.getUsers(response.accessToken);
            setUsers(userData); // Populate the users list
        } catch (error) {
            console.error('Failed to fetch users:', error);
        }
    };

    const handleAddLicense = async () => {
        if (readOnly) { return; }
        if (!newLicenseKey) {
            alert('Please enter a license key.');
            return;
        }

        try {
            const request = { ...loginRequest, account: accounts[0] };
            const response = await instance.acquireTokenSilent(request);

            const result = await LicenseDataService.addLicense(newLicenseKey, response.accessToken);

            if (result && result.status === 200) {
                fetchLicenses(); // Refresh license list
                setShowAddLicenseModal(false);
                setNewLicenseKey('');
                alert('License added successfully.');
            } else {
                alert(result.data.message);
            }
        } catch (error) {
            console.error('Failed to add license:', error);
        }
    };

    const handleAddUser = async () => {
        if (readOnly) { return; }
        if (!selectedUserId || !selectedRole || !selectedLicense) {
            alert('Please select a user, role, and license.');
            return;
        }

        const selectedUser = users.find(user => user.userId == selectedUserId);

        try {
            const request = { ...loginRequest, account: accounts[0] };
            const response = await instance.acquireTokenSilent(request);

            const result = await LicenseDataService.assignLicenseToUser(
                selectedLicense,
                selectedRole,
                selectedUser.name,
                selectedUser.email,
                response.accessToken
            );

            if (result && result.status == 200) {
                fetchLicenses(); // Refresh license list after assignment
                setShowAddUserModal(false);
                alert("License updated successfully.");
            } else {
                alert(result.data.message);
            }
        } catch (error) {
            console.error('Failed to assign license:', error);
        }
    };

    const getAvailableUserSlots = () => {
        const totalUserSlots = licenses.reduce((acc, license) => acc + license.userSlots, 0);
        const usedUserSlots = licenseAssignations.filter(x => x.accessType === 'user' && !x.available).length;
        return totalUserSlots - usedUserSlots;
    };

    const getAvailableAdminSlots = () => {
        const totalAdminSlots = licenses.reduce((acc, license) => acc + license.adminSlots, 0);
        const usedAdminSlots = licenseAssignations.filter(x => x.accessType === 'admin' && !x.available).length;
        return totalAdminSlots - usedAdminSlots;
    };

    const columns = React.useMemo(
        () => [
            { header: 'User', accessorKey: 'userContactName' },
            { header: 'Start Date', accessorKey: 'startDate', cell: info => formatDate(info.getValue()) },
            { header: 'End Date', accessorKey: 'expirationDate', cell: info => formatDate(info.getValue()) },
            { header: 'Access Type', accessorKey: 'accessType' },
            { header: 'License Type', accessorKey: 'licenseType' },
            { header: 'Email', accessorKey: 'userContactEmail' },
            {
                header: 'Edit',
                id: 'edit',
                cell: ({ row }) => (
                    <Button variant="outline-primary" size="sm">
                        <FontAwesomeIcon icon={faEdit} />
                    </Button>
                )
            },
            {
                header: 'Delete',
                id: 'delete',
                cell: ({ row }) => (
                    <Button variant="outline-danger" size="sm">
                        <FontAwesomeIcon icon={faTrash} />
                    </Button>
                )
            }
        ],
        []
    );

    return (
        <Container fluid>
            <Row className="my-3">
                <Col>
                    <h2>Current Licenses</h2>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <p>Available User licenses: {getAvailableUserSlots()} / {licenses.reduce((acc, license) => acc + license.userSlots, 0)}</p>
                            <p>Available Admin licenses: {getAvailableAdminSlots()} / {licenses.reduce((acc, license) => acc + license.adminSlots, 0)}</p>
                        </div>
                        <div>
                            <Button disabled={readOnly} className='me-2' variant="primary" onClick={() => setShowAddLicenseModal(true)}>
                                Add License
                            </Button>
                            {canListUsers && (
                                <Button disabled={readOnly} variant="secondary" onClick={() => setShowAddUserModal(true)}>
                                    Assign User
                                </Button>
                            )}
                        </div>
                    </div>
                    {/* License table */}
                    <div className='licenses-container'>
                        <Table columns={columns} data={licenseAssignations.filter(x => !x.available)} tableClass="table-hover" containerClass="table-responsive" headerClass="table-light" />
                    </div>
                </Col>
            </Row>

            {/* Add License Modal */}
            <Modal show={showAddLicenseModal} onHide={() => setShowAddLicenseModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add License</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group controlId="newLicenseKey">
                        <Form.Label>License Key</Form.Label>
                        <Form.Control disabled={readOnly}
                            type="text"
                            placeholder="Enter license key"
                            value={newLicenseKey}
                            onChange={(e) => setNewLicenseKey(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddLicenseModal(false)}>
                        Close
                    </Button>
                    <Button disabled={readOnly} variant="primary" onClick={handleAddLicense}>
                        Add License
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add User Modal */}
            <Modal show={showAddUserModal} onHide={() => setShowAddUserModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Assign User</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {users.filter(user => !licenseAssignations.map(l => l.userContactEmail).includes(user.email)).length === 0 ? (
                        <div>
                            <p>All users already have a license assigned.</p>
                            <p>
                                You can register more users in the application's <Link to="/admin/users">Security module</Link>.
                            </p>
                        </div>
                    ) : (
                        <>
                            <Form.Group controlId="selectUser">
                                <Form.Label>Select User</Form.Label>
                                <Form.Select
                                    value={selectedUserId}
                                    disabled={readOnly}
                                    onChange={(e) => setSelectedUserId(e.target.value)}
                                >
                                    <option value="">Select User</option>
                                    {users
                                        .filter(user => !licenseAssignations.map(l => l.userContactEmail).includes(user.email))
                                        .map(user => (
                                            <option key={user.userId} value={user.userId}>{user.name} ({user.email})</option>
                                        ))
                                    }
                                </Form.Select>
                            </Form.Group>
                            <Form.Group controlId="userRole" className="mt-3">
                                <Form.Label>Role</Form.Label>
                                <Form.Select
                                    value={selectedRole}
                                    disabled={readOnly}
                                    onChange={(e) => setSelectedRole(e.target.value)}
                                >
                                    <option value="">Select Role</option>
                                    <option value="admin">Admin</option>
                                    <option value="user">User</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group controlId="selectLicense" className="mt-3">
                                <Form.Label>Select License</Form.Label>
                                <Form.Select disabled={readOnly}
                                    value={selectedLicense}
                                    onChange={(e) => setSelectedLicense(e.target.value)}
                                >
                                    <option value="">Select License</option>
                                    {licenses.map(license => (
                                        <option key={license.hash} value={license.hash}>
                                            {license.company} - {license.hash.substring(0, 8)}...
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddUserModal(false)}>
                        Close
                    </Button>
                    {users.filter(user => !licenseAssignations.map(l => l.userContactEmail).includes(user.email)).length > 0 && (
                        <Button disabled={readOnly} variant="primary" onClick={handleAddUser}>
                            Assign
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default LicenseManagement;
